import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { FinancePdfViewButtonComponent } from '../../finance-pdf-view-button.component';
import { AccountsService, FinancePdfStatus } from '../../accounts.service';
import { CreditNote } from '../credit-note.model';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../accounting-lifecycle.model';
import { AccountingTabLabelComponent } from '../../shared/accounting-tab-label.component';
import { AccountingWorkflowActionsComponent } from '../../shared/accounting-workflow-actions.component';
import { AccountingAuditTimelineComponent } from '../../shared/accounting-audit-timeline.component';
import { AccountingGlPreviewPanelComponent } from '../../shared/accounting-gl-preview-panel.component';
import { AccountingAttachmentsPanelComponent } from '../../shared/accounting-attachments-panel.component';
import { ManualSourceBadgeComponent } from '../../shared/manual-source-badge.component';
import { AccountingTaxBreakdownPanelComponent } from '../../shared/accounting-tax-breakdown-panel.component';
import { ZatcaCompliancePanelComponent } from '../../shared/zatca-compliance-panel.component';
import { AccountingComplianceEvent } from '../../accounting-compliance.model';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';
import { PermissionService } from '../../../security/permission.service';
import { NewFinanceNoteService } from '../../notes/new-finance-note.service';
import { SupplierCreditClaimReview } from '../../notes/new-finance-note.model';

@Component({
    selector: 'app-credit-note-detail',
    standalone: true,
    imports: [FinancePdfViewButtonComponent, CommonModule, FormsModule, ButtonModule, TableModule, TabsModule, TagModule, InputTextModule, InputGroupModule, InputGroupAddonModule, IftaLabelModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageMetricStripComponent, AccountingTabLabelComponent, AccountingWorkflowActionsComponent, AccountingAuditTimelineComponent, AccountingGlPreviewPanelComponent, AccountingAttachmentsPanelComponent, ManualSourceBadgeComponent, AccountingTaxBreakdownPanelComponent, ZatcaCompliancePanelComponent],
    templateUrl: './credit-note-detail.component.html',
    styleUrls: ['./credit-note-detail.component.css']
})
export class CreditNoteDetailComponent implements OnInit {
    readonly documentActions = new PendingActionTracker<string>();
    note?: CreditNote;
    pdfStatus?: FinancePdfStatus;
    pdfError = '';
    workflowError = '';
    auditEvents: DocumentAuditEvent[] = [];
    complianceEvents: AccountingComplianceEvent[] = [];
    glPreview?: GlPreview;
    retryingZatca = false;
    activeTab = 'overview';
    claimReviews: SupplierCreditClaimReview[] = [];
    claimReviewReasons: Record<number, string> = {};
    reviewBusy = false;

    constructor(private route: ActivatedRoute, private router: Router, private accountsService: AccountsService,
        private notes: NewFinanceNoteService, private permissions: PermissionService) {}

    get canReviewClaims(): boolean { return this.permissions.getPermissionCodes().has('accounts.credit-note.approve'); }
    openClaim(id: number): void { this.router.navigate(['/accounts/enterprise/payables/vendor-debit-note/view', id]); }
    loadClaimReviews(): void {
        this.claimReviews = [];
        if (!this.note?.creditNoteID || this.note.documentStatus !== 'APPROVED'
            || !this.permissions.getPermissionCodes().has('accounts.vendor-debit-note.view')) return;
        this.notes.creditClaimReviews(this.note.creditNoteID).subscribe({ next: rows => this.claimReviews = rows,
            error: error => this.workflowError = error?.error?.message || 'Unable to load related claims.' });
    }
    confirmClaimUnrelated(item: SupplierCreditClaimReview): void {
        if (!this.note?.creditNoteID || !this.canReviewClaims || this.reviewBusy || item.reviewed) return;
        const reason = this.claimReviewReasons[item.id]?.trim();
        if (!reason) { this.workflowError = 'Enter why the credit and claim are unrelated.'; return; }
        this.reviewBusy = true; this.workflowError = '';
        this.notes.confirmCreditClaimUnrelated(this.note.creditNoteID, item.id, reason).subscribe({ next: () => {
            this.reviewBusy = false; delete this.claimReviewReasons[item.id]; this.loadClaimReviews();
            this.accountsService.getCreditNoteAuditEvents(this.note!.creditNoteID!).subscribe(events => this.auditEvents = events);
        }, error: error => { this.reviewBusy = false; this.workflowError = error?.error?.message || 'Unable to record the review.'; } });
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Amount', value: this.money(this.note?.creditNoteAmount), hint: 'Credit note total', tone: 'info' },
            { label: 'Open', value: this.money(this.note?.openAmount), hint: 'Remaining unapplied credit', tone: 'warn' },
            { label: 'Original bill', value: this.note?.originalBillNumber || this.note?.bill?.billNumber || '-', hint: 'Source document' },
            { label: 'Lines', value: this.note?.creditNoteLines?.length || 0, hint: 'Adjustment lines' }
        ];
    }

    get canEditDraft(): boolean { return !!this.note?.creditNoteID && isEditableFinancialDraft(this.note); }

    editDraft(): void {
        if (this.canEditDraft) this.router.navigate(['/accounts/enterprise/payables/credit-note/edit', this.note!.creditNoteID]);
    }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.accountsService.getCreditNoteById(id).subscribe(note => { this.note = note; this.loadClaimReviews(); });
        this.accountsService.financePdfStatus('credit-note', id).subscribe({ next: status => this.pdfStatus = status,
            error: () => this.pdfStatus = { status: 'MISSING', reprintAvailable: false } });
        this.accountsService.getCreditNoteAuditEvents(id).subscribe(events => this.auditEvents = events);
        this.accountsService.getComplianceEvents('CREDIT_NOTE', id).subscribe(events => this.complianceEvents = events);
        this.accountsService.getCreditNoteGlPreview(id).subscribe(preview => this.glPreview = preview);
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'lines', 'journal', 'attachments', 'tax', 'workflow', 'activity'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    money(amount?: number) { return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }); }
    workflow(action: AccountingDocumentAction, reason?: string) {
        if (!this.note?.creditNoteID || (action === 'cancel' && !isEditableFinancialDraft(this.note))) return;
        this.workflowError = '';
        this.accountsService.runCreditNoteWorkflow(this.note.creditNoteID, action, { source: 'PORTAL', reason }).subscribe({ next: () => {
            const id = this.note?.creditNoteID;
            if (id) {
                this.accountsService.getCreditNoteById(id).subscribe(note => { this.note = note; this.loadClaimReviews(); });
                this.accountsService.financePdfStatus('credit-note', id).subscribe(status => this.pdfStatus = status);
                this.accountsService.getCreditNoteAuditEvents(id).subscribe(events => this.auditEvents = events);
                this.accountsService.getCreditNoteGlPreview(id).subscribe(preview => this.glPreview = preview);
            }
        }, error: error => this.workflowError = error?.error?.message || 'Unable to update the credit note.' });
    }
    download() {
        if (!this.note?.creditNoteID || !this.documentActions.begin('download')) return;
        this.pdfError = '';
        if (this.pdfStatus?.status === 'MISSING') { this.pdfError = 'No stored issued PDF is available for this credit note.'; this.documentActions.end('download'); return; }
        this.accountsService.downloadCreditNoteDocument(this.note.creditNoteID)
            .pipe(finalize(() => this.documentActions.end('download')))
            .subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'credit-note', this.note?.creditNoteNumber, this.note?.creditNoteID, this.note?.postingStatus),
                error: () => this.pdfError = 'The credit note PDF is unavailable.' });
    }
    retryZatca() {
        if (!this.note?.creditNoteID) return;
        this.retryingZatca = true;
        this.accountsService.retryCreditNoteZatca(this.note.creditNoteID).subscribe({
            next: () => {
                this.retryingZatca = false;
                this.accountsService.getCreditNoteById(this.note!.creditNoteID!).subscribe(note => this.note = note);
                this.accountsService.getComplianceEvents('CREDIT_NOTE', this.note!.creditNoteID!).subscribe(events => this.complianceEvents = events);
            },
            error: error => { this.retryingZatca = false; this.workflowError = error?.error?.message || 'Unable to submit the credit note.'; }
        });
    }
    back() { this.router.navigate(['/accounts/enterprise/payables/credit-note']); }
}
